import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * Floating ambient music toggle.
 * - Default: muted (browsers block autoplay with sound)
 * - Click once to start playing at low volume (0.15)
 * - Click again to mute
 * Track: "Sun in Your Eyes" by Alejandro Magaña — synthwave / 80s-Tron style.
 *        Mixkit Stock Music Free License (free for commercial use, no attribution required).
 *        https://mixkit.co/license/#musicFree
 * To swap: replace TRACK_URL with any direct .mp3/.ogg link, or drop a file
 * into /app/frontend/public/ and reference it as "/your-file.mp3".
 */
const TRACK_URL = "https://assets.mixkit.co/music/131/131.mp3";
const TARGET_VOLUME = 0.15;

export default function MusicToggle() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = TARGET_VOLUME;
    a.loop = true;
    const onCanPlay = () => setReady(true);
    a.addEventListener("canplaythrough", onCanPlay);
    return () => a.removeEventListener("canplaythrough", onCanPlay);
  }, []);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      if (playing) {
        a.pause();
        setPlaying(false);
      } else {
        // gentle fade-in
        a.volume = 0;
        await a.play();
        setPlaying(true);
        const target = TARGET_VOLUME;
        const steps = 18;
        let i = 0;
        const id = setInterval(() => {
          i++;
          a.volume = Math.min(target, (target * i) / steps);
          if (i >= steps) clearInterval(id);
        }, 60);
      }
    } catch (e) {
      // Autoplay/network blocked — surface state honestly
      setPlaying(false);
    }
  };

  return (
    <>
      <audio ref={audioRef} src={TRACK_URL} preload="none" />
      <button
        type="button"
        onClick={toggle}
        data-testid="music-toggle"
        aria-label={playing ? "Mute background music" : "Play background music"}
        title={playing ? "Mute ambient track" : "Play ambient track"}
        className="music-toggle-btn"
      >
        <span className={`mt-pulse ${playing ? "is-on" : ""}`} />
        {playing ? <Volume2 size={16} /> : <VolumeX size={16} />}
        <span className="mt-label">{playing ? "Ambient · On" : "Ambient · Off"}</span>
        {!ready && !playing && <span className="mt-loading" />}
      </button>
    </>
  );
}
